'use client'

import { useSearchParams } from 'next/navigation'
import { type FC, type PropsWithChildren } from 'react'
import { InstantSearchNext } from 'react-instantsearch-nextjs'

import { useProxySearchClient } from '../../../algolia'
import { useDeviceDetectorContext } from '../../../device-detector'
import { useExpaContext } from '../../../expa'
import { useExperimentAdServerSerpTopResults } from '../../../experiments/ad-server-serp-top-results/useExperimentAdServerSerpTopResults'
import { useSetSearchSessionStorageValuesForOptimizelyTracking } from '../../../experiments/hooks'
import {
  PRODUCT_SEARCH_HITS_PER_PAGE,
  productSearchOptimizeSearchQueryIfItHasPzns,
  ProductSearchVirtualWidgetSearchBox,
  useProductSearchAlgoliaRouting,
  useProductSearchContext,
} from '../../../product-search'
import {
  ConfigureWithNeural,
  useHeaderSearchAlgoliaFilters,
} from '../../../standard-shop-layout'

import { getSearchPageAlgoliaSearchAnalyticsTags } from './getSearchPageAlgoliaSearchAnalyticsTags'

export const SearchPageAlgoliaSearch: FC<PropsWithChildren> = ({
  children,
}) => {
  const searchParams = useSearchParams()
  const ruleContexts = useExpaContext() ?? 'direct'

  const {
    productSearchConfig: { baseAlgoliaConfig },
  } = useProductSearchContext()
  const searchClient = useProxySearchClient()
  const { deviceType } = useDeviceDetectorContext()

  const { productsAlgoliaIndex } = baseAlgoliaConfig

  const searchQueryInitial = searchParams.get('q') ?? searchParams.get('query')

  const filters = useHeaderSearchAlgoliaFilters()
  const { isPznQuery } =
    productSearchOptimizeSearchQueryIfItHasPzns(searchQueryInitial)

  useSetSearchSessionStorageValuesForOptimizelyTracking()

  /**
   * Experiment for extending search radius for sponsored products
   * https://jira.shop-apotheke.com/browse/RMADT-3482
   */
  useExperimentAdServerSerpTopResults()
  const userToken = searchParams.get('userToken')

  return (
    <InstantSearchNext
      future={{
        preserveSharedStateOnUnmount: true,
      }}
      indexName={productsAlgoliaIndex}
      routing={useProductSearchAlgoliaRouting()}
      searchClient={searchClient}
    >
      <ConfigureWithNeural
        analytics={userToken !== null}
        analyticsTags={getSearchPageAlgoliaSearchAnalyticsTags(
          deviceType,
          searchParams,
        )}
        clickAnalytics
        disableNeuralSearch={isPznQuery}
        enableABTest={userToken !== null}
        facetingAfterDistinct
        filters={filters}
        getRankingInfo
        hitsPerPage={PRODUCT_SEARCH_HITS_PER_PAGE}
        ruleContexts={[ruleContexts]}
        userToken={userToken ?? undefined}
      />
      {children}

      {/*
      The search box is not part of the current page, it's located in the header.
      This virtual widget is used to preserve search parameters in the URL for routing.
      */}
      <ProductSearchVirtualWidgetSearchBox />
    </InstantSearchNext>
  )
}
